import type { ComponentNode } from '@api/types.gen'

/**
 * The Vue `:key` of every node in one children list.
 *
 * A User Component reference is keyed by the User Component it places rather
 * than by its own nodeId, so its instance survives a page change. Two references
 * to the same User Component in one list would then share a key, and the built
 * pipe — which patches one page's children into the next instead of remounting —
 * leaves the first old copy mounted with stale DOM until a reload. So the second
 * and later references carry their occurrence: the nth copy on one page is still
 * reused as the nth copy on the next.
 */
export function siblingRenderKeys(children: readonly ComponentNode[]): string[] {
  const occurrences = new Map<string, number>()
  return children.map((node) => {
    const sharedNodeId = node.type === 'SharedNodeRef' ? node.fields?.sharedNodeId : undefined
    if (typeof sharedNodeId !== 'string' || !sharedNodeId) return node.nodeId
    const base = `sn-${sharedNodeId}`
    const occurrence = (occurrences.get(base) ?? 0) + 1
    occurrences.set(base, occurrence)
    return occurrence === 1 ? base : `${base}#${occurrence}`
  })
}
